import { ProfessionalGroup } from "src/types/snugtotal";

/**
 * Determines whether the currently logged-in identity is allowed to update
 * estate planning documents for the selected household.
 *
 * Two independent professional-group flags govern this, plus a
 * professional-own-household carve-out:
 *
 * - `allow_client_doc_updates` only locks down document updates when the
 *   CLIENT THEMSELVES is logged in (i.e. the real, authenticated identity
 *   has no professional_group role at all). It never restricts an agent or
 *   admin who is logged in and acting on a client's behalf.
 * - `allow_agents_doc_updates` separately blocks agents (PRO_GROUP_AGENT)
 *   who are acting on a client's behalf, when disabled.
 * - Admins (PRO_GROUP_ADMIN) acting on a client's behalf are always allowed
 *   to update documents, regardless of either flag.
 *
 * `ownerRole` should come from the real logged-in identity
 * (`ownerUserData.professional_group_role_user_data?.role`), not the
 * effective household identity — an agent/admin "acting as" a client still
 * authenticates as themselves.
 */
export const getAllowClientDocUpdates = ({
  isProfessionalOwnHousehold,
  professionalGroup,
  ownerRole,
  defaultAllowClientDocUpdates,
}: {
  isProfessionalOwnHousehold: boolean;
  professionalGroup: ProfessionalGroup | null | undefined;
  ownerRole: "PRO_GROUP_ADMIN" | "PRO_GROUP_AGENT" | null | undefined;
  defaultAllowClientDocUpdates: boolean;
}): boolean => {
  // Professional-account households (head of household is also their own
  // professional advisor — i.e. our direct customers) are never blocked by
  // either client- or agent-facing document update flags.
  if (isProfessionalOwnHousehold) return true;

  const isClientLoggedIn = !ownerRole;
  const isAgent = ownerRole === "PRO_GROUP_AGENT";

  const blockedByClientFlag =
    isClientLoggedIn &&
    !(professionalGroup?.allow_client_doc_updates ?? defaultAllowClientDocUpdates);

  const blockedByAgentFlag =
    isAgent && (professionalGroup?.allow_agents_doc_updates ?? true) === false;

  return !blockedByClientFlag && !blockedByAgentFlag;
};
